// Connection Status Component - Shows SIP and ARI connection status
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useAsterisk } from '../hooks/useAsterisk';

const RINGTONE_OUTPUT_DEVICE_ID_KEY = 'frontOfficeNew_ringtoneOutputDeviceId';
const RINGTONE_OUTPUT_DEVICE_LABEL_KEY = 'frontOfficeNew_ringtoneOutputDeviceLabel';
const RINGTONE_OUTPUT_CHANGED_EVENT = 'frontOfficeNew:ringtone-output-changed';
const CALL_AUDIO_OUTPUT_DEVICE_ID_KEY = 'frontOfficeNew_callAudioOutputDeviceId';
const CALL_AUDIO_OUTPUT_DEVICE_LABEL_KEY = 'frontOfficeNew_callAudioOutputDeviceLabel';
const CALL_AUDIO_OUTPUT_CHANGED_EVENT = 'frontOfficeNew:call-audio-output-changed';

const ConnectionStatus = ({ theme = 'light', embedded = false }) => {
  const { status: sipStatus, registered: sipRegistered } = useSoftphone();
  const { isConnected: ariConnected } = useAsterisk();
  const isDark = theme === 'dark';
  const [position, setPosition] = useState(() => {
    const fallback = { top: 10, left: 10 };
    if (typeof window === 'undefined') return fallback;
    return { top: 10, left: Math.max(10, window.innerWidth - 220) };
  });
  const [ringtoneOutputs, setRingtoneOutputs] = useState([]);
  const [ringtoneOutputLabel, setRingtoneOutputLabel] = useState(() => {
    try {
      return localStorage.getItem(RINGTONE_OUTPUT_DEVICE_LABEL_KEY) || '';
    } catch {
      return '';
    }
  });
  const [ringtoneOutputError, setRingtoneOutputError] = useState('');
  const [callAudioOutputLabel, setCallAudioOutputLabel] = useState(() => {
    try {
      return localStorage.getItem(CALL_AUDIO_OUTPUT_DEVICE_LABEL_KEY) || '';
    } catch {
      return '';
    }
  });
  const [callAudioOutputError, setCallAudioOutputError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const loadRingtoneOutputs = useCallback(async () => {
    if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
      setRingtoneOutputs([]);
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter((device) => device.kind === 'audiooutput');
      setRingtoneOutputs(outputs);
      return outputs;
    } catch (error) {
      setRingtoneOutputError(error?.message || 'Unable to load audio output devices.');
      return [];
    }
  }, []);

  const getStatusColor = (status) => {
    if (status === 'Registered' || status === true) return '#4caf50'; // Green
    if (status === 'Connecting' || status === 'Registering') return '#ff9800'; // Orange
    if (status === 'connected') return '#4caf50';
    if (status === 'connecting') return '#ff9800';
    return '#f44336'; // Red
  };

  const onMouseDown = (e) => {
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: position.left,
      origY: position.top
    };
  };

  useEffect(() => {
    if (embedded) return undefined;

    const onMove = (e) => {
      if (!dragRef.current.dragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({
        left: Math.max(0, dragRef.current.origX + dx),
        top: Math.max(0, dragRef.current.origY + dy)
      });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [embedded, position.left, position.top]);

  useEffect(() => {
    loadRingtoneOutputs().catch(() => {});
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return undefined;

    const handleDeviceChange = () => loadRingtoneOutputs().catch(() => {});
    navigator.mediaDevices.addEventListener?.('devicechange', handleDeviceChange);
    return () => navigator.mediaDevices.removeEventListener?.('devicechange', handleDeviceChange);
  }, [loadRingtoneOutputs]);

  const saveRingtoneOutput = (device) => {
    const deviceId = String(device?.deviceId || '').trim();
    if (!deviceId) return;

    const deviceLabel = String(device?.label || 'Selected speaker').trim();
    localStorage.setItem(RINGTONE_OUTPUT_DEVICE_ID_KEY, deviceId);
    localStorage.setItem(RINGTONE_OUTPUT_DEVICE_LABEL_KEY, deviceLabel);
    setRingtoneOutputLabel(deviceLabel);
    setRingtoneOutputError('');
    window.dispatchEvent(new CustomEvent(RINGTONE_OUTPUT_CHANGED_EVENT, {
      detail: { deviceId, label: deviceLabel }
    }));
  };

  const handleRingtoneOutputChange = (event) => {
    const device = ringtoneOutputs.find((output) => output.deviceId === event.target.value);
    if (device) saveRingtoneOutput(device);
  };

  const handleSelectRingtoneOutput = async () => {
    setRingtoneOutputError('');
    try {
      if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.selectAudioOutput !== 'function') {
        return;
      }

      const device = await navigator.mediaDevices.selectAudioOutput();
      saveRingtoneOutput(device);
      loadRingtoneOutputs().catch(() => {});
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') return;
      setRingtoneOutputError(error?.message || 'Unable to select ringtone speaker.');
    }
  };

  const saveCallAudioOutput = (device) => {
    const deviceId = String(device?.deviceId || '').trim();
    if (!deviceId) return;

    const deviceLabel = String(device?.label || 'Selected headset').trim();
    localStorage.setItem(CALL_AUDIO_OUTPUT_DEVICE_ID_KEY, deviceId);
    localStorage.setItem(CALL_AUDIO_OUTPUT_DEVICE_LABEL_KEY, deviceLabel);
    setCallAudioOutputLabel(deviceLabel);
    setCallAudioOutputError('');
    window.dispatchEvent(new CustomEvent(CALL_AUDIO_OUTPUT_CHANGED_EVENT, {
      detail: { deviceId, label: deviceLabel }
    }));
  };

  const handleCallAudioOutputChange = (event) => {
    const device = ringtoneOutputs.find((output) => output.deviceId === event.target.value);
    if (device) saveCallAudioOutput(device);
  };

  const handleSelectCallAudioOutput = async () => {
    setCallAudioOutputError('');
    try {
      if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.selectAudioOutput !== 'function') {
        return;
      }

      const device = await navigator.mediaDevices.selectAudioOutput();
      saveCallAudioOutput(device);
      loadRingtoneOutputs().catch(() => {});
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') return;
      setCallAudioOutputError(error?.message || 'Unable to select call headset.');
    }
  };

  return (
    <div
      className={embedded ? 'frontoffice-profile-connection' : ''}
      style={embedded ? undefined : {
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        background: isDark ? '#172033' : 'white',
        color: isDark ? '#e5edf8' : '#212529',
        padding: '10px 15px',
        borderRadius: '8px',
        boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.45)' : '0 2px 10px rgba(0,0,0,0.1)',
        zIndex: 9999,
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        cursor: 'grab',
        userSelect: 'none'
      }}
    >
      <div
        className={embedded ? 'frontoffice-profile-connection-title' : ''}
        style={embedded ? undefined : { marginBottom: '8px', fontWeight: 'bold', cursor: 'grab' }}
        onMouseDown={embedded ? undefined : onMouseDown}
      >
        Connection Status
      </div>
      
      {/* SIP Status */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: getStatusColor(sipRegistered ? 'Registered' : sipStatus),
          marginRight: '8px'
        }}></div>
        <span>
          <strong>SIP:</strong> {sipRegistered ? 'Registered ✓' : sipStatus}
        </span>
      </div>

      {/* ARI Status */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: getStatusColor(ariConnected),
          marginRight: '8px'
        }}></div>
        <span>
          <strong>ARI:</strong> {ariConnected ? 'Connected ✓' : 'Disconnected'}
        </span>
      </div>

      {/* Extension Info */}
      <div style={{ marginTop: '8px', fontSize: '11px', color: isDark ? '#a8b3c7' : '#666' }}>
        {(() => {
          try {
            const ext = JSON.parse(localStorage.getItem('extension') || '{}');
            const activeExtension = ext.sipUsername || ext.extensionNumber || ext.extension || '';
            return activeExtension ? `Ext: ${activeExtension}` : 'No extension';
          } catch {
            return 'No extension';
          }
        })()}
      </div>

      {embedded && (
        <button
          type="button"
          className="dropdown-item frontoffice-profile-settings-toggle"
          onClick={() => setSettingsOpen((open) => !open)}
          aria-expanded={settingsOpen}
        >
          <i className="bi bi-gear me-2"></i>
          <span>Settings</span>
          <i className={`bi ${settingsOpen ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`}></i>
        </button>
      )}

      {(!embedded || settingsOpen) && (
        <div className={embedded ? 'frontoffice-profile-audio-settings' : ''}>
          <label htmlFor="ringtone-output-device" className="d-block mt-2 mb-1" style={{ fontSize: '10px' }}>
            Ringtone Output
          </label>
          <select
            id="ringtone-output-device"
            className="form-select form-select-sm"
            value={localStorage.getItem(RINGTONE_OUTPUT_DEVICE_ID_KEY) || ''}
            onFocus={() => loadRingtoneOutputs().catch(() => {})}
            onChange={handleRingtoneOutputChange}
            style={{ cursor: 'pointer', fontSize: '10px' }}
          >
            <option value="">Select speaker</option>
            {ringtoneOutputs.map((device, index) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Audio output ${index + 1}`}
              </option>
            ))}
          </select>

          {typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.selectAudioOutput === 'function' && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100 mt-2 d-flex align-items-center justify-content-center gap-1"
              onClick={handleSelectRingtoneOutput}
              title="Select the loudspeaker used for incoming-call ringtone"
              style={{ cursor: 'pointer', fontSize: '11px' }}
            >
              <i className="bi bi-volume-up-fill"></i>
              {ringtoneOutputLabel ? 'Change Ringtone Speaker' : 'Select Ringtone Speaker'}
            </button>
          )}
          {ringtoneOutputLabel && (
            <div
              className="text-truncate mt-1"
              title={ringtoneOutputLabel}
              style={{ maxWidth: '190px', fontSize: '10px', color: isDark ? '#a8b3c7' : '#666' }}
            >
              Ring: {ringtoneOutputLabel}
            </div>
          )}
          {ringtoneOutputError && (
            <div className="text-danger mt-1" style={{ maxWidth: '190px', fontSize: '10px' }}>
              {ringtoneOutputError}
            </div>
          )}

          <label htmlFor="call-audio-output-device" className="d-block mt-2 mb-1" style={{ fontSize: '10px' }}>
            Call Audio Output
          </label>
          <select
            id="call-audio-output-device"
            className="form-select form-select-sm"
            value={localStorage.getItem(CALL_AUDIO_OUTPUT_DEVICE_ID_KEY) || ''}
            onFocus={() => loadRingtoneOutputs().catch(() => {})}
            onChange={handleCallAudioOutputChange}
            style={{ cursor: 'pointer', fontSize: '10px' }}
          >
            <option value="">Select headset</option>
            {ringtoneOutputs.map((device, index) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Audio output ${index + 1}`}
              </option>
            ))}
          </select>

          {typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.selectAudioOutput === 'function' && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100 mt-2 d-flex align-items-center justify-content-center gap-1"
              onClick={handleSelectCallAudioOutput}
              title="Select the headset used after accepting a call"
              style={{ cursor: 'pointer', fontSize: '11px' }}
            >
              <i className="bi bi-headset"></i>
              {callAudioOutputLabel ? 'Change Call Headset' : 'Select Call Headset'}
            </button>
          )}
          {callAudioOutputLabel && (
            <div
              className="text-truncate mt-1"
              title={callAudioOutputLabel}
              style={{ maxWidth: '190px', fontSize: '10px', color: isDark ? '#a8b3c7' : '#666' }}
            >
              Call: {callAudioOutputLabel}
            </div>
          )}
          {callAudioOutputError && (
            <div className="text-danger mt-1" style={{ maxWidth: '190px', fontSize: '10px' }}>
              {callAudioOutputError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
